import { reportClientError } from "#src/api/ReportClientError.ts";
import { BorderButton } from "#src/components/buttons/BorderButton.tsx";
import { Component, type ErrorInfo, type ReactNode } from "react";
interface Props {
  error?: Error;
  children?: ReactNode;
}

interface State {
  error: Error | null;
  reportStatus: "idle" | "pending" | "sent" | "failed";
}

export default class ErrorBoundry extends Component<Props, State> {
  state: State = { error: null, reportStatus: "idle" };
  private reportId = 0;
  private reportedError: unknown;

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(error, errorInfo);
    this.report(error, errorInfo.componentStack);
  }

  componentDidMount() {
    if (this.props.error) {
      this.report(this.props.error);
    }
  }

  componentDidUpdate(previousProps: Props) {
    if (this.props.error && this.props.error !== previousProps.error) {
      this.report(this.props.error);
    }
  }

  componentWillUnmount() {
    this.reportId++;
  }

  private report(error: unknown, componentStack?: string) {
    if (error === this.reportedError) {
      return;
    }
    this.reportedError = error;
    const reportId = ++this.reportId;
    this.setState({ reportStatus: "pending" });
    void reportClientError(error, componentStack).then((success) => {
      if (reportId === this.reportId) {
        this.setState({ reportStatus: success ? "sent" : "failed" });
      }
    });
  }

  render() {
    if (this.state.error || this.props.error) {
      const error = this.state.error || this.props.error;

      if (!error) {
        return null;
      }

      let message = "";

      if (typeof error === "string") {
        message = error;
      } else if (error instanceof Error) {
        message = error.message;

        if (message.includes("Minified React error #")) {
          message = "Error loading page.";
        }
      } else if (typeof error === "object") {
        try {
          message = JSON.stringify(error);
        } catch {
          message = "Unknown error object";
        }
      } else {
        message = String(error);
      }

      return (
        <div className="flex h-screen items-center justify-center">
          <div className="max-w-sm px-4">
            <button
              className="text-empty m-0 mb-2 block w-full cursor-pointer border-none bg-transparent p-0 text-left text-sm"
              onClick={() => window.location.reload()}
            >
              <i aria-hidden className="far fa-circle-arrow-left" /> Go Back
            </button>
            <h1 className="mb-0">We’re sorry!</h1>
            <h3 className="text-empty">Something’s gone wrong.</h3>
            {error && <p className="text-error">{message}</p>}
            <p>
              Our team has been notified with details about the error. If you
              would like to give us some more feedback about what happened,
              please fill out an error report.
            </p>
            <BorderButton
              type="submit"
              onClick={() =>
                (window.location.href =
                  "mailto:support@captyn.com?subject=Customer%20Support")
              }
            >
              Contact Support
            </BorderButton>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
